import { useEffect } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useAuth } from "@/features/auth";
import type { PusherTicketsAvailableEvent } from "@/features/boutique/types/pusher.type";
import { getUserChatId } from "@/features/chat/utils/getUserChatId";
import { usePush } from "@/shared/hooks/usePush";
import { useUser } from "@/shared/hooks/useUser";
import type { CardEventType, CardEventData } from "@/shared/types/events.type";

type NotificationConfig = {
  channel: string
  events: Array<{
    name: string
    handler: (data: unknown) => void
  }>
};

export const EventNotificationHandler = () => {
  const { subscribe, unsubscribe } = usePush();
  const { id: userId } = useUser();
  const { user } = useAuth();
  const navigate = useNavigate();

  const chatUserIdRaw = user?.employee?.key_chat
    ? getUserChatId(user.employee.key_chat)
    : undefined;

  const userIdString = userId !== null ? String(userId) : undefined;
  const chatUserId = chatUserIdRaw !== null ? String(chatUserIdRaw) : undefined;

  const onCardNotification = (eventType: CardEventType, { success }: CardEventData) => {
    const getNotificationContent = (type: CardEventType) => {
      const events: Record<CardEventType, { title: string, description: string }> = {
        card_activate: {
          title: success ? "Carte activée" : "Erreur d'activation de carte",
          description: success
            ? "Votre carte a été activée avec succès"
            : "Nous n'avons pas réussi à activer votre carte, veuillez réessayer",
        },
        card_lock: {
          title: success ? "Carte verrouillée" : "Erreur de verrouillage de carte",
          description: success
            ? "Votre carte a été verrouillée avec succès"
            : "Nous n'avons pas réussi à verrouiller votre carte, veuillez réessayer",
        },
        card_unlock: {
          title: success ? "Carte déverrouillée" : "Erreur de déverrouillage de carte",
          description: success
            ? "Votre carte a été déverrouillée avec succès"
            : "Nous n'avons pas réussi à déverrouiller votre carte, veuillez réessayer",
        },
      };

      return events[type];
    };

    const { title, description } = getNotificationContent(eventType);
    if (!success) toast[success ? "success" : "error"](title, { description });
  };

  const getCardNotificationConfig = (channel?: string): NotificationConfig[] => {
    if (!channel) return [];

    return [
      {
        channel,
        events: [
          {
            name: "card_activate",
            handler: (data: unknown) => onCardNotification("card_activate", data as CardEventData),
          },
          {
            name: "card_lock",
            handler: (data: unknown) => onCardNotification("card_lock", data as CardEventData),
          },
          {
            name: "card_unlock",
            handler: (data: unknown) => onCardNotification("card_unlock", data as CardEventData),
          },
        ],
      },
    ];
  };

  const onTicketsAvailable = (data: PusherTicketsAvailableEvent) => {
    const ticketCount = Number(data.ticket_count);
    const isPlural = ticketCount > 1;
    const orderId = data.order_id;

    toast.info(
      isPlural ? "Vos billets sont prêts !" : "Votre billet est prêt !",
      {
        description: `${ticketCount} billet${isPlural ? "s" : ""} disponible${isPlural ? "s" : ""} au téléchargement`,
        action: {
          label: "Voir",
          onClick: () => navigate(`/boutique/commandes/${orderId}`),
        },
        className: "tickets-toast",
      },
    );
  };

  const getTicketsNotificationConfig = (userChatId?: string): NotificationConfig[] => {
    if (!userChatId) return [];

    return [
      {
        channel: `utilisateur-${userChatId}`,
        events: [
          {
            name: "tickets-available",
            handler: (data: unknown) => onTicketsAvailable(data as PusherTicketsAvailableEvent),
          },
        ],
      },
    ];
  };

  useEffect(() => {
    const notificationConfigs: NotificationConfig[] = [
      ...getCardNotificationConfig(userIdString),
      ...getTicketsNotificationConfig(chatUserId),
    ];

    const subscriptions: Array<{ channel: string, event: string, handler: (data: unknown) => void }> = [];

    notificationConfigs.forEach((config) => {
      config.events.forEach((event) => {
        subscribe(config.channel, event.name, event.handler);
        subscriptions.push({
          channel: config.channel,
          event: event.name,
          handler: event.handler,
        });
      });
    });

    return () => {
      subscriptions.forEach(({ channel, event, handler }) => {
        unsubscribe(channel, event, handler);
      });
    };
  }, [subscribe, unsubscribe, userIdString, chatUserId]);

  return null;
};
